import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Film, Play, Sparkles, Loader2, ChevronRight, RefreshCw, ArrowLeft,
  FileVideo, AlertTriangle, Check,
} from 'lucide-react';
import UploadZone from './UploadZone';
import DraftPlayer from './DraftPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTH_TOKEN_KEY = 'alivan-auth-token';
const authHeaders = () => {
  try {
    const t = localStorage.getItem(AUTH_TOKEN_KEY);
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch (_) { return {}; }
};

const STATE_META = {
  detected:          { label: 'Sin renderizar', color: 'text-white/70', dot: 'bg-white/40' },
  proposed:          { label: 'Planificado',    color: 'text-cyan-300', dot: 'bg-cyan-400' },
  working:           { label: 'Renderizando',   color: 'text-yellow-300', dot: 'bg-yellow-400 animate-pulse' },
  draft_ready:       { label: 'Borrador listo', color: 'text-emerald-300', dot: 'bg-emerald-400' },
  changes_requested: { label: 'Cambios solicitados', color: 'text-yellow-300', dot: 'bg-yellow-400' },
  approved:          { label: 'Aprobado',       color: 'text-emerald-300', dot: 'bg-emerald-400' },
  completed:         { label: 'Completado',     color: 'text-emerald-300', dot: 'bg-emerald-500' },
  failed:            { label: 'Fallido',        color: 'text-red-300', dot: 'bg-red-500' },
  executing:         { label: 'Ejecutando',     color: 'text-yellow-300', dot: 'bg-yellow-400 animate-pulse' },
  rejected:          { label: 'Rechazado',      color: 'text-red-300', dot: 'bg-red-500' },
};

const jsonFetch = async (url, opts = {}) => {
  const r = await fetch(url, {
    credentials: 'include',
    ...opts,
    headers: {
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders(),
      ...(opts.headers || {}),
    },
  });
  const data = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, data };
};

// --- New Project inline form ---------------------------------------------
const NewProjectForm = ({ onCreated, onCancel }) => {
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [duration, setDuration] = useState('');
  const [format, setFormat] = useState('16:9');
  const [presetSlug, setPresetSlug] = useState('default');
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await jsonFetch(`${API}/production/presets`);
      if (!cancelled && ok) setPresets(data?.presets || []);
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    setErr('');
    if (!title.trim()) { setErr('El título es obligatorio.'); return; }
    setBusy(true);
    const body = {
      title: title.trim(),
      project_slug: presetSlug || 'default',
      instructions: instructions.trim(),
      target_duration_s: duration ? Number(duration) : null,
      target_format: format || null,
    };
    const { ok, data } = await jsonFetch(`${API}/production/projects`, {
      method: 'POST', body: JSON.stringify(body),
    });
    setBusy(false);
    if (!ok) { setErr(typeof data?.detail === 'string' ? data.detail : 'No se pudo crear el proyecto'); return; }
    onCreated?.(data);
  };

  return (
    <div className="border border-neon-red/30 bg-black/40 rounded p-4" data-testid="prod-new-project-form">
      <div className="text-neon-red/90 text-[10px] font-jetbrains uppercase tracking-widest mb-3">
        Nuevo proyecto de producción
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-jetbrains text-white/50 uppercase tracking-widest">Título*</label>
          <input
            data-testid="prod-new-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ej: Reel de la semana, Mazal Kids ep. 3…"
            className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-sm text-white"
            maxLength={200}
          />
        </div>
        <div>
          <label className="text-[10px] font-jetbrains text-white/50 uppercase tracking-widest">
            Instrucciones para AliVan (opcional)
          </label>
          <textarea
            data-testid="prod-new-instructions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={3}
            placeholder="Ej: quiero un corte enérgico de 45s, prioriza los planos del atardecer, deja aire al final…"
            className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-sm text-white"
            maxLength={4000}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-jetbrains text-white/50 uppercase tracking-widest">
              Duración objetivo (seg)
            </label>
            <input
              data-testid="prod-new-duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value.replace(/[^\d]/g, ''))}
              placeholder="45"
              className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-sm text-white"
            />
          </div>
          <div>
            <label className="text-[10px] font-jetbrains text-white/50 uppercase tracking-widest">
              Formato
            </label>
            <select
              data-testid="prod-new-format"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-sm text-white"
            >
              <option value="16:9">16:9 (horizontal)</option>
              <option value="9:16">9:16 (vertical / reels)</option>
              <option value="1:1">1:1 (cuadrado)</option>
              <option value="4:5">4:5 (feed)</option>
            </select>
          </div>
        </div>
        {presets.length > 0 && (
          <div>
            <label className="text-[10px] font-jetbrains text-white/50 uppercase tracking-widest">
              Preset estético
            </label>
            <select
              data-testid="prod-new-preset"
              value={presetSlug}
              onChange={(e) => setPresetSlug(e.target.value)}
              className="w-full mt-1 bg-black/60 border border-white/20 rounded p-2 text-sm text-white"
            >
              {presets.map(p => (
                <option key={p.slug} value={p.slug}>{p.display_name}</option>
              ))}
            </select>
          </div>
        )}
        {err && <div className="text-[11px] text-red-300" data-testid="prod-new-error">{err}</div>}
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="text-[11px] px-3 py-1 border border-white/20 text-white/60 rounded"
            data-testid="prod-new-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={busy}
            className="text-[11px] px-3 py-1 border border-neon-red/50 text-neon-red hover:bg-neon-red/20 rounded flex items-center gap-1 disabled:opacity-50"
            data-testid="prod-new-submit"
          >
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Crear proyecto
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Project row --------------------------------------------------------
const ProjectRow = ({ project, onOpen }) => {
  const meta = STATE_META[project.state] || STATE_META.detected;
  const count = project.source_files?.length || 0;
  return (
    <div
      data-testid={`prod-project-row-${project.job_id}`}
      className="border border-neon-red/20 bg-black/30 rounded p-3 mb-2 hover:border-neon-red/50 transition-colors cursor-pointer"
      onClick={() => onOpen(project.job_id)}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${meta.dot}`} />
        <span className={`text-[10px] font-jetbrains uppercase tracking-widest ${meta.color}`}>
          {meta.label}
        </span>
        {project.draft_ready && (
          <span className="text-[9px] font-jetbrains uppercase tracking-widest text-emerald-400 border border-emerald-500/40 rounded px-1">
            v{project.draft_version} listo
          </span>
        )}
        <span className="ml-auto text-[10px] text-white/40">
          {new Date(project.updated_at).toLocaleString()}
        </span>
      </div>
      <div className="text-sm text-white font-medium mt-1 truncate">{project.title}</div>
      <div className="flex items-center gap-3 mt-1 text-[10px] text-white/50">
        <span><FileVideo className="inline w-3 h-3 mr-1" />{count} fuente(s)</span>
        {project.target_duration_s && <span>· objetivo {project.target_duration_s}s</span>}
        {project.target_format && <span>· {project.target_format}</span>}
        <ChevronRight className="ml-auto w-3 h-3 text-white/30" />
      </div>
    </div>
  );
};

// --- Project detail -----------------------------------------------------
const ProjectDetail = ({ jobId, onBack, onChanged, setNotice, setError }) => {
  const [project, setProject] = useState(null);
  const [plan, setPlan] = useState(null);
  const [renderStatus, setRenderStatus] = useState(null);
  const [versions, setVersions] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [busy, setBusy] = useState(null); // 'plan' | 'render' | 'revision' | null
  const pollRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => { isMounted.current = true; return () => { isMounted.current = false; }; }, []);

  const refresh = useCallback(async () => {
    if (!jobId) return;
    const [p, pl, rs, vs] = await Promise.all([
      jsonFetch(`${API}/production/projects/${jobId}`),
      jsonFetch(`${API}/production/projects/${jobId}/plan`),
      jsonFetch(`${API}/production/projects/${jobId}/render`),
      jsonFetch(`${API}/production/projects/${jobId}/versions`),
    ]);
    if (!isMounted.current) return;
    if (p.ok) setProject(p.data);
    if (pl.ok) setPlan(pl.data);
    if (rs.ok) setRenderStatus(rs.data);
    if (vs.ok) {
      setVersions(vs.data);
      // Default to the newest version whenever selection is out of range.
      const latest = vs.data?.latest_version;
      setSelectedVersion((cur) => {
        if (!latest) return null;
        const known = (vs.data?.drafts || []).map(d => d.version);
        if (cur && known.includes(cur)) return cur;
        return latest;
      });
    }
  }, [jobId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while render is active or state indicates work in progress.
  useEffect(() => {
    const active = renderStatus?.active ||
      ['working', 'executing'].includes(project?.state);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (active) {
      pollRef.current = setInterval(refresh, 3500);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [renderStatus?.active, project?.state, refresh]);

  const sources = project?.source_files || [];
  const hasPlan = plan?.has_plan;
  const drafts = versions?.drafts || [];
  const selectedDraft = selectedVersion
    ? drafts.find(d => d.version === selectedVersion)
    : (drafts.length ? drafts[drafts.length - 1] : null);
  const draft = selectedDraft || renderStatus?.draft;
  const qcLatest = versions?.qc_latest;
  const continuity = versions?.continuity;

  const runPlan = async () => {
    setBusy('plan'); setError(''); setNotice('AliVan y sus agentes están planificando…');
    const { ok, data } = await jsonFetch(`${API}/production/projects/${jobId}/plan`, { method: 'POST' });
    setBusy(null);
    if (!ok) {
      const msg = data?.detail?.message || data?.detail || 'No se pudo planificar';
      setError(typeof msg === 'string' ? msg : 'Error al planificar');
      setNotice('');
      return;
    }
    setNotice('Plan y timeline generados. Ya puedes renderizar.');
    setTimeout(() => setNotice(''), 3000);
    await refresh();
    onChanged?.();
  };

  const runRender = async () => {
    setBusy('render'); setError(''); setNotice('Enviando a renderizado…');
    const { ok, data } = await jsonFetch(`${API}/production/projects/${jobId}/render`, { method: 'POST' });
    setBusy(null);
    if (!ok) {
      setError(typeof data?.detail === 'string' ? data.detail : 'No se pudo iniciar el render');
      setNotice('');
      return;
    }
    setNotice('Render en curso. Iré actualizando el progreso.');
    setTimeout(() => setNotice(''), 3000);
    await refresh();
    onChanged?.();
  };

  const approveDraft = async () => {
    const { ok, data } = await jsonFetch(`${API}/work-center/jobs/${jobId}/approve`, {
      method: 'POST', body: JSON.stringify({ comment: null }),
    });
    if (!ok) { setError(data?.detail || 'No se pudo aprobar'); return; }
    setNotice('Borrador aprobado. Queda como referencia en el Centro de Trabajo.');
    setTimeout(() => setNotice(''), 3000);
    await refresh();
    onChanged?.();
  };

  const requestChanges = async (comment) => {
    // Sub-fase 2C: creates a NEW revision (v+1) without overwriting older
    // versions. The backend keeps the request note in the audit trail.
    setBusy('revision');
    setError('');
    setNotice('Solicitando revisión — se generará una nueva versión.');
    const { ok, data } = await jsonFetch(
      `${API}/production/projects/${jobId}/request-revision`,
      { method: 'POST', body: JSON.stringify({ note: comment }) },
    );
    setBusy(null);
    if (!ok) {
      setError(typeof data?.detail === 'string' ? data.detail : 'No se pudo pedir la revisión');
      setNotice('');
      return;
    }
    setTimeout(() => setNotice(''), 3500);
    await refresh();
    onChanged?.();
  };

  const reRender = async () => {
    // Just kick off another render pass using the existing timeline.
    await runRender();
  };

  if (!project) {
    return (
      <div className="text-[11px] text-white/50 italic" data-testid="prod-detail-loading">Cargando proyecto…</div>
    );
  }

  const meta = STATE_META[project.state] || STATE_META.detected;
  const progressHistory = renderStatus?.progress || [];
  const lastProgress = progressHistory[progressHistory.length - 1];

  return (
    <div data-testid={`prod-detail-${jobId}`}>
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={onBack}
          className="text-[11px] px-2 py-1 border border-white/20 text-white/60 hover:bg-white/10 rounded flex items-center gap-1"
          data-testid="prod-detail-back"
        >
          <ArrowLeft className="w-3 h-3" />Proyectos
        </button>
        <button
          onClick={refresh}
          className="text-[11px] p-1 text-white/60 hover:text-white"
          data-testid="prod-detail-refresh"
          title="Refrescar"
        >
          <RefreshCw className="w-3 h-3" />
        </button>
        <span className="ml-auto flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${meta.dot}`} />
          <span className={`text-[10px] font-jetbrains uppercase tracking-widest ${meta.color}`}>
            {meta.label}
          </span>
        </span>
      </div>

      <div className="mb-3">
        <div className="text-white font-outfit text-base" data-testid="prod-detail-title">{project.title}</div>
        {project.instructions && (
          <div className="text-[11px] text-white/60 mt-1 italic">"{project.instructions}"</div>
        )}
        <div className="flex flex-wrap gap-3 text-[10px] text-white/40 mt-1">
          {project.target_duration_s && <span>Duración objetivo: {project.target_duration_s}s</span>}
          {project.target_format && <span>Formato: {project.target_format}</span>}
          <span>Creado: {new Date(project.created_at).toLocaleString()}</span>
        </div>
      </div>

      {/* Upload zone */}
      <div className="mb-4">
        <UploadZone
          jobId={jobId}
          sourceCount={sources.length}
          onUploaded={() => { refresh(); onChanged?.(); }}
          onError={(m) => setError(m)}
        />
      </div>

      {/* Source list */}
      {sources.length > 0 && (
        <div className="mb-4 border border-white/10 bg-black/20 rounded p-3" data-testid="prod-source-list">
          <div className="text-[10px] font-jetbrains uppercase tracking-widest text-white/50 mb-2">
            Material fuente ({sources.length})
          </div>
          <ul className="space-y-1 text-[11px]">
            {sources.map((s) => (
              <li key={s.file_id} className="flex items-center gap-2 text-white/70" data-testid={`prod-source-item-${s.file_id}`}>
                <span className="text-[9px] font-jetbrains uppercase text-neon-red/70 border border-neon-red/30 rounded px-1">
                  {s.kind}
                </span>
                <span className="truncate flex-1">{s.filename}</span>
                {s.duration_s != null && <span className="text-white/40">{Math.round(s.duration_s)}s</span>}
                {s.resolution && <span className="text-white/40">{s.resolution}</span>}
                {s.probe_error && (
                  <span className="text-yellow-300" title={s.probe_error}>
                    <AlertTriangle className="w-3 h-3" />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions bar: plan / render */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={runPlan}
          disabled={busy === 'plan' || sources.length === 0}
          className="text-[11px] px-3 py-1.5 border border-cyan-500/50 text-cyan-300 hover:bg-cyan-500/20 rounded flex items-center gap-1 disabled:opacity-40"
          data-testid="prod-plan-btn"
          title={sources.length === 0 ? 'Sube al menos un clip primero' : 'Genera plan narrativo + timeline con los agentes'}
        >
          {busy === 'plan' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {hasPlan ? 'Re-planificar' : 'Planificar edición'}
        </button>
        <button
          onClick={runRender}
          disabled={busy === 'render' || !hasPlan || renderStatus?.active}
          className="text-[11px] px-3 py-1.5 border border-neon-red/50 text-neon-red hover:bg-neon-red/20 rounded flex items-center gap-1 disabled:opacity-40"
          data-testid="prod-render-btn"
          title={!hasPlan ? 'Primero planifica la edición' : 'Renderiza con FFmpeg'}
        >
          {busy === 'render' || renderStatus?.active ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          {renderStatus?.active ? 'Renderizando…' : (draft ? 'Volver a renderizar' : 'Renderizar')}
        </button>
      </div>

      {/* Progress panel while working */}
      {(renderStatus?.active || project.state === 'working') && (
        <div className="border border-yellow-500/30 bg-yellow-500/10 rounded p-3 mb-4" data-testid="prod-render-progress">
          <div className="flex items-center gap-2 text-[11px] text-yellow-200">
            <Loader2 className="w-3 h-3 animate-spin" />
            AliVan está montando el video…
            {lastProgress?.stage && <span className="text-yellow-300/80">· {lastProgress.stage}</span>}
          </div>
          {progressHistory.length > 0 && (
            <ul className="mt-2 text-[10px] text-yellow-100/70 space-y-0.5 max-h-32 overflow-y-auto">
              {progressHistory.map((p, i) => (
                <li key={i}>
                  <span className="text-yellow-300/70">{p.stage}</span>
                  {p.message && <> — {p.message}</>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Story/timeline summary once we have a plan */}
      {plan?.story_plan && (
        <details className="border border-white/10 rounded p-3 mb-4 bg-black/20" data-testid="prod-plan-summary">
          <summary className="text-[10px] font-jetbrains uppercase tracking-widest text-white/60 cursor-pointer">
            Plan narrativo (agente Story Editor)
          </summary>
          <div className="mt-2 text-[11px] text-white/70">
            {plan.story_plan.logline && (
              <div className="mb-2"><span className="text-white/40">Logline:</span> {plan.story_plan.logline}</div>
            )}
            {plan.story_plan.beats && plan.story_plan.beats.length > 0 && (
              <ul className="list-disc list-inside space-y-0.5">
                {plan.story_plan.beats.slice(0, 12).map((b, i) => (
                  <li key={i}>{typeof b === 'string' ? b : (b?.description || JSON.stringify(b))}</li>
                ))}
              </ul>
            )}
          </div>
        </details>
      )}

      {/* Failed state */}
      {project.state === 'failed' && (
        <div className="border border-red-500/40 bg-red-500/10 rounded p-3 mb-4 text-[11px] text-red-200" data-testid="prod-render-failed">
          <div className="flex items-center gap-1 mb-1">
            <AlertTriangle className="w-3 h-3" />
            El último render falló.
          </div>
          <div className="text-red-200/70 text-[10px]">
            Puedes ajustar la Bible / instrucciones y volver a planificar o renderizar.
          </div>
        </div>
      )}

      {/* Version selector — visible whenever we have at least one draft */}
      {drafts.length > 0 && (
        <div className="border border-neon-red/30 bg-black/30 rounded p-3 mb-3" data-testid="prod-version-selector">
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-[10px] font-jetbrains uppercase tracking-widest text-neon-red/80">
              Versiones ({drafts.length})
            </span>
            {drafts.map((d) => {
              const active = d.version === (selectedDraft?.version);
              return (
                <button
                  key={d.version}
                  onClick={() => setSelectedVersion(d.version)}
                  data-testid={`prod-version-btn-v${d.version}`}
                  className={`text-[11px] px-2 py-1 rounded border ${
                    active
                      ? 'border-neon-red text-neon-red bg-neon-red/10'
                      : 'border-white/20 text-white/70 hover:bg-white/10'
                  }`}
                >
                  v{d.version}
                </button>
              );
            })}
            <span className="ml-auto text-[10px] text-white/50">
              Comparación reproducible: cada versión conserva su archivo y su QC.
            </span>
          </div>
        </div>
      )}

      {/* Continuity + QC compact reports for the selected version */}
      {(continuity || qcLatest) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
          {continuity && (
            <div className="border border-white/10 bg-black/20 rounded p-2" data-testid="prod-continuity-summary">
              <div className="text-[9px] uppercase font-jetbrains text-white/50 tracking-widest">
                Continuity Supervisor — {continuity.decision}
              </div>
              {continuity.summary && (
                <div className="text-[11px] text-white/70 mt-1">{continuity.summary}</div>
              )}
              {(continuity.issues || []).slice(0, 3).map((i, idx) => (
                <div key={idx} className="text-[10px] text-white/60 mt-0.5">
                  · [{i.severity}] {i.area}: {i.message}
                </div>
              ))}
            </div>
          )}
          {qcLatest && (
            <div className="border border-white/10 bg-black/20 rounded p-2" data-testid="prod-qc-summary">
              <div className="text-[9px] uppercase font-jetbrains text-white/50 tracking-widest">
                Quality Control — {qcLatest.decision}
                {qcLatest.iteration && ` (iter ${qcLatest.iteration})`}
              </div>
              {qcLatest.summary && (
                <div className="text-[11px] text-white/70 mt-1">{qcLatest.summary}</div>
              )}
              {(qcLatest.issues || []).slice(0, 3).map((i, idx) => (
                <div key={idx} className="text-[10px] text-white/60 mt-0.5">
                  · [{i.severity}] {i.message}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Draft player if we have a rendered draft */}
      {draft && (
        <DraftPlayer
          jobId={jobId}
          draft={draft}
          onApprove={approveDraft}
          onRequestChanges={requestChanges}
          onReRender={reRender}
        />
      )}
    </div>
  );
};

// --- Main Production Panel ---------------------------------------------
const ProductionPanel = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const listPollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { ok, data } = await jsonFetch(`${API}/production/projects`);
    setLoading(false);
    if (ok && Array.isArray(data)) setProjects(data);
    else if (!ok) setError('No se pudo cargar la lista de proyectos');
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll list when a project is actively working (only from list view).
  useEffect(() => {
    const anyWorking = projects.some(p => ['working', 'executing'].includes(p.state));
    if (listPollRef.current) { clearInterval(listPollRef.current); listPollRef.current = null; }
    if (!selectedId && anyWorking) {
      listPollRef.current = setInterval(load, 6000);
    }
    return () => {
      if (listPollRef.current) clearInterval(listPollRef.current);
    };
  }, [projects, selectedId, load]);

  const onCreated = (proj) => {
    setCreating(false);
    setNotice(`Proyecto "${proj.title}" creado. Sube tus clips para empezar.`);
    setTimeout(() => setNotice(''), 3500);
    setSelectedId(proj.job_id);
    load();
  };

  return (
    <div data-testid="prod-panel">
      {/* Notices */}
      {notice && (
        <div data-testid="prod-notice" className="text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1 mb-3 flex items-center gap-1">
          <Check className="w-3 h-3" />{notice}
        </div>
      )}
      {error && (
        <div data-testid="prod-error" className="text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1 mb-3 flex items-start justify-between gap-2">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-200/70">×</button>
        </div>
      )}

      {selectedId ? (
        <ProjectDetail
          jobId={selectedId}
          onBack={() => { setSelectedId(null); load(); }}
          onChanged={load}
          setNotice={setNotice}
          setError={setError}
        />
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Film className="w-4 h-4 text-neon-red" />
            <div className="text-white font-outfit text-sm">Estudio audiovisual de AliVan</div>
            <button
              onClick={load}
              className="ml-auto text-[11px] p-1 text-white/60 hover:text-white"
              data-testid="prod-list-refresh"
              title="Refrescar"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setCreating((c) => !c)}
              className="text-[11px] px-2 py-1 border border-neon-red/50 text-neon-red hover:bg-neon-red/20 rounded flex items-center gap-1"
              data-testid="prod-new-project-btn"
            >
              <Plus className="w-3 h-3" />Nuevo proyecto
            </button>
          </div>

          <div className="text-[10px] text-white/40 mb-3 font-jetbrains uppercase tracking-widest">
            Sube tu material → AliVan y sus agentes planifican y montan un borrador. Nunca publica sin tu aprobación.
          </div>

          {creating && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="mb-4"
            >
              <NewProjectForm onCreated={onCreated} onCancel={() => setCreating(false)} />
            </motion.div>
          )}

          {loading && projects.length === 0 ? (
            <div className="text-[11px] text-white/40 italic" data-testid="prod-list-loading">Cargando proyectos…</div>
          ) : projects.length === 0 ? (
            <div className="text-[11px] text-white/40 italic border border-white/10 rounded p-4 text-center" data-testid="prod-list-empty">
              Aún no hay proyectos de producción. Toca <span className="text-neon-red">Nuevo proyecto</span> para empezar.
            </div>
          ) : (
            <div data-testid="prod-list">
              {projects.map((p) => (
                <ProjectRow key={p.job_id} project={p} onOpen={setSelectedId} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductionPanel;
